import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, Link as LinkIcon } from 'lucide-react'
import {
  PiIdentificationCard,
  PiHandshake,
  PiNotepad,
  PiCalendarStar,
  PiHandCoins,
  PiEnvelope,
  PiCirclesThreePlus,
} from 'react-icons/pi'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { MobileColors } from 'styles/mobileTokens'
import { formatDate2 } from 'utils/formatting'
import { MobileAccountAttributesSummary } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileDealsAccordion } from 'components/MobileCompanyPage/MobileDealsAccordion'
import { MobileEventHistoryAccordion } from 'components/MobileCompanyPage/MobileEventHistory'
import { MobileRemindersAccordion } from 'components/MobileCompanyPage/MobileReminders'
import { CustomCompanyDataDrawer } from 'components/MobileCompanyPage/CustomCompanyDataDrawer'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getIdWithPrefix } from 'utils/pathUtils'
import { ContactCompanyType } from 'models/contact_companies'
import { BackButton } from 'components/Mobile/Mobile.styles'

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #07311e;
  overflow-y: auto;
  padding-bottom: 200px;
`

const CompanyHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const CustomLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${MobileColors.textSecondary};
  text-transform: uppercase;
  margin-bottom: 4px;

  svg {
    font-size: 14px;
  }
`

const CompanyName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${MobileColors.textPrimary};
  margin: 0 0 8px 0;
`

const CompanyMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const MetaDivider = styled.span`
  color: ${MobileColors.borderDefault};
`

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${MobileColors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  padding: 12px 10px;
  background: ${MobileColors.primary};
  border-bottom: 1px solid #07311e;
`

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px;
  background: ${MobileColors.primaryActive};
  border: none;
  border-radius: 8px;
  margin: 3px;
  color: #ffffff;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 20px;
    color: #ffffff;
  }

  &:hover {
    background: ${MobileColors.primary};
  }

  &:active {
    background: ${MobileColors.primaryDark};
  }
`

const ActionCount = styled.span`
  font-size: 11px;
  font-weight: 300;
`

const ContentSection = styled.div`
  padding: 12px;
  background: #07311e;
`

const SectionDivider = styled.div`
  height: 8px;
  background: ${MobileColors.backgroundOverlay};
`

export function MobileCustomCompanyPage() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()

  const companyID = urlParams?.company_id
    ? getIdWithPrefix(
        urlParams?.company_id,
        preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]
      )
    : undefined

  const api = apiService()
  const { data: companyData, isLoading } = api.useGetCompany(companyID || 0)

  // Set document title
  useDocumentTitle(`${companyData?.name || 'Not Found'} (Company) - First Bite`)

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!companyData) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Company not found</p>
        </div>
      </PageContainer>
    )
  }

  // For custom companies, chainProxyId is the company.id
  const chainProxyId = companyData.id
  const companyPrefix =
    preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated] || ''

  return (
    <>
      <PageContainer>
        {/* Back Button */}
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>

        {/* Company Header */}
        <CompanyHeaderSection>
          {/* Custom Label */}
          <CustomLabel>
            <PiCirclesThreePlus />
            CUSTOM
          </CustomLabel>

          {/* Company Name */}
          <CompanyName>{companyData.name}</CompanyName>

          {/* Meta Row: Updated Date, Social Icons */}
          <CompanyMetaRow>
            {companyData.modified && (
              <span>Updated {formatDate2(companyData.modified)}</span>
            )}
            {companyData.domain && (
              <>
                <MetaDivider>•</MetaDivider>
                <SocialLink
                  href={
                    companyData.domain.startsWith('http')
                      ? companyData.domain
                      : `https://${companyData.domain}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit website"
                >
                  <LinkIcon />
                </SocialLink>
              </>
            )}
          </CompanyMetaRow>
        </CompanyHeaderSection>

        {/* Action Buttons */}
        <ActionButtonsRow>
          <ActionButton
            onClick={() =>
              navigate(`/contacts?contact_company_id=${chainProxyId}`)
            }
          >
            <PiIdentificationCard />
            <ActionCount>({companyData.contact_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(`/deals?contact_company_id=${chainProxyId}`)
            }
          >
            <PiHandshake />
            <ActionCount>({companyData.deal_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/notes?contact_company_id=${chainProxyId}&company_name=${encodeURIComponent(companyData.name || '')}`
              )
            }
          >
            <PiNotepad />
            <ActionCount>({companyData.note_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(`/reminders?contact_company_id=${chainProxyId}`)
            }
          >
            <PiCalendarStar />
            <ActionCount>(0)</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/samples?contact_company_id=${chainProxyId}&company_name=${encodeURIComponent(companyData.name || '')}`
              )
            }
          >
            <PiHandCoins />
            <ActionCount>(0)</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/company/${companyPrefix}${chainProxyId}/emails?contact_company_id=${chainProxyId}`
              )
            }
          >
            <PiEnvelope />
            <ActionCount>({companyData.email_count || 0})</ActionCount>
          </ActionButton>
        </ActionButtonsRow>

        {/* Account Attributes Section */}
        <ContentSection>
          <MobileAccountAttributesSummary
            chainProxyId={chainProxyId}
            chainName={`${companyPrefix}${chainProxyId}`}
          />
        </ContentSection>

        <SectionDivider />

        {/* Deals Overview Section */}
        <ContentSection>
          <MobileDealsAccordion chainProxyId={chainProxyId} />
        </ContentSection>

        <SectionDivider />

        {/* Reminders Section */}
        <ContentSection>
          <MobileRemindersAccordion chainProxyId={chainProxyId} />
        </ContentSection>

        <SectionDivider />

        {/* Event History Section */}
        <ContentSection>
          <MobileEventHistoryAccordion chainProxyId={chainProxyId} />
        </ContentSection>
      </PageContainer>

      {/* Custom Company Data Drawer */}
      <CustomCompanyDataDrawer
        chainProxyId={chainProxyId}
        companyName={companyData.name}
        companyType={companyData.sub_category?.name}
        companyCategory={companyData.category}
        unmatchedDoorsCount={companyData.doors_count}
        createdDate={companyData.created}
        modifiedDate={companyData.modified}
        onViewDoorsClick={() => setShowDoorsModal(true)}
        onEditClick={() =>
          navigate(`/company/${companyPrefix}${chainProxyId}/edit`)
        }
      />

      {/* Unmatched Doors Modal */}
      <UnmatchedDoorsModal
        show={showDoorsModal}
        onHide={() => setShowDoorsModal(false)}
        forChainProxyId={chainProxyId}
        chainData={{
          id: chainProxyId,
          name: companyData.name,
          domain: companyData.domain,
        }}
        subCategory={companyData.sub_category}
        companyName={companyData.name}
      />
    </>
  )
}
